import { ToastProvider, TooltipProvider } from '@agently/ui';
import * as Sentry from '@sentry/react';
import App from 'App';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v7';
import { createBrowserRouter, Navigate, ScrollRestoration } from 'react-router';
import RequireAuth from 'RequireAuth';
import routes from 'routes';
import { lazyWithPreload } from '@agently/utils';
import { RootErrorBoundary } from '@agently/components';

import { assistantRoutes } from 'pages/assistant/routes';
import { hubLoader } from 'pages/hub/route-loader';

// Lazy load pages with preload capability for background prefetching
const TodayPage = lazyWithPreload(() => import('pages/today/page'));
// Today is the home screen for signed-in users: when they open it, start fetching
// it at startup so the lazy chunk doesn't add a round trip. Deep links to other
// pages skip this. Rendering retries on failure.
if (
  localStorage.getItem('authToken') &&
  window.location.pathname === routes.todayPath
) {
  TodayPage.preload().catch(() => {});
}
const LoginPage = lazyWithPreload(() => import('pages/auth/login/page'));
const AuthPage = lazyWithPreload(() => import('pages/auth/page'));
const ResetPasswordPage = lazyWithPreload(
  () => import('pages/auth/reset/page'),
);
const SetPasswordPage = lazyWithPreload(
  () => import('pages/auth/setPassword/page'),
);
const SignupPage = lazyWithPreload(() => import('pages/auth/signup/page'));
const OnboardingCompleteProfile = lazyWithPreload(
  () => import('pages/onboarding/_components/CompleteProfile'),
);
const OnboardingNotifications = lazyWithPreload(
  () => import('pages/onboarding/_components/RequestPermissions'),
);
const OnboardingSetGoal = lazyWithPreload(
  () => import('pages/onboarding/_components/SetFinancialGoal'),
);
const ChatPage = lazyWithPreload(() => import('pages/chat/page'));
const CoursePage = lazyWithPreload(() => import('pages/course/page'));
const EventsPage = lazyWithPreload(() => import('pages/events/page'));
const HubPage = lazyWithPreload(() => import('pages/hub/page'));
const ManualPage = lazyWithPreload(() => import('pages/manual/page'));
const Messages = lazyWithPreload(() => import('pages/messages/page'));
const ProfilePage = lazyWithPreload(() => import('pages/profile/page'));
const SetupGoalPage = lazyWithPreload(() => import('pages/progress/goal/page'));
const NewTransactionPage = lazyWithPreload(
  () => import('pages/progress/new/page'),
);
const EditTransactionPage = lazyWithPreload(
  () => import('pages/progress/edit/page'),
);
const ProgressPage = lazyWithPreload(() => import('pages/progress/page'));
const SocialSuccess = lazyWithPreload(() => import('pages/social-success'));
const SupportFormPage = lazyWithPreload(
  () => import('pages/support/form/page'),
);
const SupportPage = lazyWithPreload(() => import('pages/support/page'));
const TeamPage = lazyWithPreload(() => import('pages/team/page'));
const TrainingPage = lazyWithPreload(() => import('pages/training/page'));
const AuthWrapper = lazyWithPreload(
  () => import('pages/auth/_components/Wrapper'),
);
const OnboardingWrapper = lazyWithPreload(() => import('OnboardingWrapper'));

const Wrapper = () => {
  return (
    <>
      <ScrollRestoration />
      <TooltipProvider>
        <NuqsAdapter>
          <ToastProvider>
            <App />
          </ToastProvider>
        </NuqsAdapter>
      </TooltipProvider>
    </>
  );
};

// Create router function that must be called AFTER Sentry.init()
// This ensures wrapCreateBrowserRouter is called after Sentry initialization
export const createRouter = () => {
  const sentryCreateBrowserRouter =
    Sentry.wrapCreateBrowserRouter(createBrowserRouter);

  return sentryCreateBrowserRouter([
    {
      // Root error boundary - handles all routes
      errorElement: <RootErrorBoundary />,
      element: <Wrapper />,
      children: [
        // Legacy redirect for old recovery path
        {
          element: (
            <Navigate
              replace
              to={{ pathname: routes.setPasswordPath, search: location.search }}
            />
          ),
          path: '/recovery',
        },
        // Protected routes
        {
          element: <RequireAuth />,
          children: [
            { element: <TodayPage />, path: routes.todayPath },
            { element: <ProgressPage />, path: routes.progressPath },
            {
              element: <NewTransactionPage />,
              path: routes.newTransactionPath,
            },
            {
              element: <EditTransactionPage />,
              path: routes.editTransactionPath,
            },
            { element: <SetupGoalPage />, path: routes.setupGoalPath },
            {
              element: <HubPage />,
              loader: hubLoader,
              path: routes.resourcesPath,
            },
            {
              element: <Navigate replace to={routes.resourcesPath} />,
              path: routes.hubPath,
            },
            { element: <SupportPage />, path: routes.supportPath },
            { element: <SupportFormPage />, path: routes.supportFormPath },
            { element: <ProfilePage />, path: routes.profilePath },
            { element: <CoursePage />, path: routes.coursePath },
            { element: <ChatPage />, path: routes.chatPath },
            { element: <TrainingPage />, path: routes.trainingPath },
            { element: <Messages />, path: routes.messages },
            { element: <ManualPage />, path: routes.manualPath },
            { element: <EventsPage />, path: routes.eventsPath },
            { element: <TeamPage />, path: routes.teamPath },
            ...assistantRoutes,
          ],
        },
        // Onboarding — requires auth, shown before entering the main app
        {
          element: <OnboardingWrapper />,
          children: [
            {
              path: routes.onboardingPath,
              children: [
                {
                  index: true,
                  element: (
                    <Navigate
                      replace
                      to={routes.onboardingCompleteProfilePath}
                    />
                  ),
                },
                {
                  element: <OnboardingCompleteProfile />,
                  path: 'complete-profile',
                },
                {
                  element: <OnboardingNotifications />,
                  path: 'notifications',
                },
                {
                  element: <OnboardingSetGoal />,
                  path: 'set-goal',
                },
              ],
            },
          ],
        },
        // Auth routes (public)
        {
          element: <AuthWrapper />,
          children: [
            {
              element: <AuthPage />,
              path: routes.authPath,
              children: [
                { element: <LoginPage />, path: routes.signinPath },
                { element: <SignupPage />, path: routes.signupPath },
                { element: <SetPasswordPage />, path: routes.setPasswordPath },
                {
                  element: <ResetPasswordPage />,
                  path: routes.resetPasswordPath,
                },
              ],
            },
          ],
        },
        // Public pages
        { element: <SocialSuccess />, path: routes.socialSuccessPublishPath },
        // Catch-all redirect to today
        { element: <Navigate replace to={routes.todayPath} />, path: '*' },
      ],
    },
  ]);
};
